/**
 * services/adaptive/adaptiveSetupWeights.ts
 *
 * Adaptive Setup Weighting — MER Algo Footprints v7.1B
 *
 * Converts expectancy-matrix learning into context-aware adaptive
 * edge weights. A meta-layer / ranking-interpretation layer only.
 * Never changes engine weights or setup generation logic.
 *
 * Non-executing. Decision support only.
 */

import type { ExpectancyMatrixSummary, ExpectancyMatrixCell } from '../quant/expectancyMatrix';
import type { DecisionQualitySummary } from '../quant/decisionQuality';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdaptiveSetupContextWeight {
  setupType:      string;
  regime:         string;
  phase:          string;
  rawExpectancy:  number | null;
  adjustedWeight: number;          // 0.75–1.25, interpretive only
  sampleStrength: 'weak' | 'moderate' | 'strong';
  bias:           'favored' | 'neutral' | 'deprioritized';
  note:           string;
}

export interface AdaptiveSetupWeightsSummary {
  contexts:             AdaptiveSetupContextWeight[];
  favoredContexts:      AdaptiveSetupContextWeight[];    // top 5 by adjustedWeight
  deprioritizedContexts: AdaptiveSetupContextWeight[];   // bottom 5 by adjustedWeight
  summary:              string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sampleStrength(count: number): AdaptiveSetupContextWeight['sampleStrength'] {
  if (count >= 10) return 'strong';
  if (count >= 5)  return 'moderate';
  return 'weak';
}

function computeWeight(cell: ExpectancyMatrixCell): {
  weight: number;
  bias:   AdaptiveSetupContextWeight['bias'];
  note:   string;
} {
  const ss = sampleStrength(cell.count);

  // With insufficient data, stay neutral regardless of raw expectancy
  if (cell.quality === 'insufficient_data') {
    return { weight: 1.0, bias: 'neutral', note: 'Insufficient sample — no adjustment.' };
  }

  const e  = cell.expectancy ?? 0;
  const wr = cell.winRate ?? 0;

  // Conservative caps: max ±0.25
  let weight: number;
  let bias:   AdaptiveSetupContextWeight['bias'];
  let note:   string;

  if (cell.quality === 'high' && e >= 0.5 && wr >= 0.5) {
    // Strong sample + good expectancy + good win rate
    weight = ss === 'strong' ? 1.25 : ss === 'moderate' ? 1.15 : 1.08;
    bias   = 'favored';
    note   = `High-edge context (E=${e.toFixed(2)}R, WR=${(wr * 100).toFixed(0)}%, n=${cell.count}).`;
  } else if (cell.quality === 'medium' && e > 0) {
    weight = ss === 'strong' ? 1.10 : ss === 'moderate' ? 1.05 : 1.02;
    bias   = 'favored';
    note   = `Positive edge (E=${e.toFixed(2)}R, n=${cell.count}).`;
  } else if (cell.quality === 'low') {
    weight = ss === 'strong' ? 0.75 : ss === 'moderate' ? 0.82 : 0.88;
    bias   = 'deprioritized';
    note   = `Negative edge (E=${e.toFixed(2)}R, n=${cell.count}). Historically weak context.`;
  } else {
    // medium + zero expectancy or neutral quality
    weight = 1.0;
    bias   = 'neutral';
    note   = `Neutral edge (E=${e.toFixed(2)}R, n=${cell.count}).`;
  }

  return { weight: +weight.toFixed(2), bias, note };
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeAdaptiveSetupWeights(
  matrix: ExpectancyMatrixSummary,
  _dqsSummary?: DecisionQualitySummary,   // reserved for future DQS-driven adjustments
): AdaptiveSetupWeightsSummary {

  const contexts: AdaptiveSetupContextWeight[] = matrix.cells.map(cell => {
    const { weight, bias, note } = computeWeight(cell);
    return {
      setupType:      cell.setupType,
      regime:         cell.regime,
      phase:          cell.phase,
      rawExpectancy:  cell.expectancy,
      adjustedWeight: weight,
      sampleStrength: sampleStrength(cell.count),
      bias,
      note,
    };
  });

  // Sort by adjustedWeight desc for favored, asc for deprioritized
  const sorted     = [...contexts].sort((a, b) => b.adjustedWeight - a.adjustedWeight);
  const favored    = sorted.filter(c => c.bias === 'favored').slice(0, 5);
  const deprio     = [...contexts].sort((a, b) => a.adjustedWeight - b.adjustedWeight)
    .filter(c => c.bias === 'deprioritized').slice(0, 5);

  const summary = contexts.length === 0
    ? 'No contextual edge data available yet.'
    : favored.length > 0
    ? `${favored.length} favored context${favored.length > 1 ? 's' : ''} identified. ` +
      `${deprio.length > 0 ? `${deprio.length} deprioritized context${deprio.length > 1 ? 's' : ''} flagged.` : ''}`
    : 'Insufficient edge data for adaptive weighting — weights remain neutral.';

  return { contexts, favoredContexts: favored, deprioritizedContexts: deprio, summary };
}
